import React, { useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileText, CheckCircle2, AlertTriangle, Download } from "lucide-react";

const SAMPLE_CSV = `name,email,designation,department,manager_email,photo_url,location,start_date
Eleanor Whitfield,eleanor@contoso.com,CEO,Executive,,,Cape Town,2018-04-01
Marcus Chen,marcus@contoso.com,CTO,Engineering,eleanor@contoso.com,,Cape Town,2019-06-15
Sofia Reyes,sofia@contoso.com,VP Engineering,Engineering,marcus@contoso.com,,Cape Town,2020-09-01
`;

export default function BulkImportDialog({ open, onOpenChange, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setBusy(false);
    setCommitting(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setBusy(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/employees/bulk-import?dry_run=true", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not parse CSV");
      reset();
    }
    setBusy(false);
  };

  const commit = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/employees/bulk-import?dry_run=false", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const msg = `Imported ${data.inserted} new · ${data.updated} updated · ${data.manager_links} reporting lines`;
      toast.success(msg);
      onDone?.();
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    }
    setCommitting(false);
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "organogram-sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const det = preview?.detected_columns;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: '"Work Sans", sans-serif' }}>Bulk import from CSV</DialogTitle>
          <DialogDescription className="text-xs text-neutral-600">
            Upload a CSV with one row per employee. Existing employees (matched by email) are updated; new ones are inserted.
            Use the <strong>manager_email</strong> column to link reporting lines automatically.
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-neutral-300 p-8 text-center">
              <UploadCloud size={28} className="mx-auto mb-3 text-neutral-400"/>
              <p className="text-sm text-neutral-700">Drop your CSV here, or</p>
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="mt-3 rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]"
                data-testid="bulk-import-choose-file-button"
              >
                {busy ? "Parsing…" : "Choose CSV file"}
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={onFileChange}
                className="hidden"
                data-testid="bulk-import-file-input"
              />
            </div>
            <button
              onClick={downloadSample}
              className="flex items-center gap-1 text-xs text-[#002FA7] hover:underline"
              data-testid="download-sample-csv"
            >
              <Download size={11}/> Download sample CSV
            </button>
            <div className="border-l-2 border-neutral-300 bg-neutral-50 p-3 text-[11px] text-neutral-700">
              <strong>Expected columns</strong> (case-insensitive, all optional except <code>name</code>):
              <code className="ml-1 font-mono">name · email · designation · department · manager_email · photo_url</code>.
              Any extra columns are stored as custom data on the employee.
            </div>
          </div>
        )}

        {preview && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-px border border-neutral-200 bg-neutral-200">
              <SummaryTile label="Rows" value={preview.total_rows}/>
              <SummaryTile label="Errors" value={preview.errors.length} bad/>
              <SummaryTile label="Custom cols" value={det?.custom_data?.length || 0}/>
            </div>

            {/* Detected columns */}
            <div className="border border-neutral-200 p-3">
              <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                Detected columns
              </p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  ["Name", det?.name, true],
                  ["Email", det?.email],
                  ["Designation", det?.designation],
                  ["Department", det?.department],
                  ["Manager email", det?.manager_email],
                  ["Photo URL", det?.photo_url],
                ].map(([label, col, required]) => (
                  <Badge
                    key={label}
                    variant="outline"
                    className={`rounded-none text-[10px] uppercase ${
                      col ? "border-[#0A7029] text-[#0A7029]" : required ? "border-[#C50F1F] text-[#C50F1F]" : "border-neutral-300 text-neutral-400"
                    }`}
                  >
                    {col ? <CheckCircle2 size={9} className="mr-1"/> : null}
                    {label}{col ? ` → ${col}` : " (missing)"}
                  </Badge>
                ))}
              </div>
              {det?.custom_data?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {det.custom_data.map((c) => (
                    <Badge key={c} variant="outline" className="rounded-none border-neutral-300 text-[10px] uppercase">
                      +custom · {c}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Errors */}
            {preview.errors.length > 0 && (
              <div className="border border-[#D83B01] bg-orange-50 p-3">
                <p className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#D83B01]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                  <AlertTriangle size={11}/> {preview.errors.length} rows with issues
                </p>
                <ul className="text-xs text-neutral-700">
                  {preview.errors.slice(0, 5).map((e, i) => (
                    <li key={i}>Row {e.row}: {e.error}</li>
                  ))}
                  {preview.errors.length > 5 && <li>…{preview.errors.length - 5} more</li>}
                </ul>
              </div>
            )}

            {/* Preview rows */}
            {preview.preview?.length > 0 && (
              <div className="border border-neutral-200">
                <p className="border-b border-neutral-200 px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                  First {preview.preview.length} rows
                </p>
                <ul>
                  {preview.preview.map((r, i) => (
                    <li key={i} className="border-b border-neutral-100 px-3 py-2 text-xs last:border-0">
                      <p className="font-medium">{r.name}</p>
                      <p className="truncate text-[11px] text-neutral-600">
                        {r.designation || "—"} · {r.department || "—"} · {r.email || "no email"} {r.manager_email ? `→ reports to ${r.manager_email}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {preview ? (
            <>
              <Button variant="outline" onClick={reset} className="rounded-none border-neutral-300" data-testid="bulk-import-cancel-button">
                Change file
              </Button>
              <Button
                onClick={commit}
                disabled={committing || preview.total_rows === 0}
                className="rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]"
                data-testid="bulk-import-commit-button"
              >
                <FileText size={14} className="mr-2"/>
                {committing ? "Importing…" : `Import ${preview.total_rows} rows`}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={close} className="rounded-none border-neutral-300">
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryTile({ label, value, bad }) {
  return (
    <div className="bg-white px-4 py-3">
      <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
        {label}
      </p>
      <p className={`mt-1 text-2xl tracking-tight ${bad && value > 0 ? "text-[#D83B01]" : ""}`} style={{ fontFamily: '"Work Sans", sans-serif' }}>
        {value}
      </p>
    </div>
  );
}
